import { v4 as Uuid } from 'uuid'
import { VYBY_EFFECTS } from '@/etc/consts'
import { jclone } from '@/etc/utils'
import { toStorage, fromStorage } from '@/etc/storage'
import GLOBAL_EFFECT_PRESETS from '@/editor/effects/globalEffectPresets'
import GLOBAL_TEXT_MOTION_PRESETS from '@/editor/text/globalTextMotionPresets'
import {
  buildEffectCatalog,
  serializeUserEffectCatalog,
} from '@/editor/effects/effectPresets'
import { LOG } from '@/etc/log'
import { factory, tracksIndex, defaultSong, defaultEnv, defaultPicture, defaultSettings, defaultTrack, defaultTimeline } from '../state'
import {
  fitPercentToRawScale,
  readStripFitScaleBaseline,
} from '@/editor/transform/fitScale'
import {
  buildTransformKeyframePresetApplyFrames,
  normalizeTransformKeyframePreset,
} from '@/editor/transform/transformKeyframePresets'
import {
  canonicalParamColorCode,
  normalizeParamColor,
} from '@/editor/paramColors'

const log = LOG.m('store::project::core')

const TRANSFORM_PROP_CODES = [
  'position.x',
  'position.y',
  'position.z',
  'rotation.x',
  'rotation.y',
  'rotation.z',
  'scale.x',
  'scale.y',
  'scale.z',
]

const EASING_TO_EDITOR = {
  linear: 'Linear.None',
  ease_in: 'Quadratic.In',
  ease_out: 'Quadratic.Out',
  ease_in_out: 'Quadratic.InOut',
  hold: 'Linear.None',
}

const finiteNum = (value, fallback = 0) => {
  const next = Number(value)
  return Number.isFinite(next) ? next : fallback
}

const clampNum = (value, min, max) => Math.min(max, Math.max(min, value))

const isPlainObject = (value) =>
  !!value && typeof value === 'object' && !Array.isArray(value)

export default {
  _pushUndo(state, label = '') {
    try {
      const snap = {
        label,
        tracks: jclone(state.tracks),
        timeline: jclone(state.timeline),
        settings: jclone(state.settings)
      }
      state._history.undo.push(snap)
      const MAX = 50
      if (state._history.undo.length > MAX) state._history.undo.shift()
      state._history.redo = []
    } catch (e) { /* ignore snapshot errors */ }
  },
  _restoreSnapshot(state, snap) {
    if (!snap) return
    state.tracks = jclone(snap.tracks)
    state.timeline = jclone(snap.timeline)
    state.settings = jclone(snap.settings)
    state.tuned = false
    state.saved = false
  },
  setGlobalPresetCatalog: (state, data = {}) => {
    const effectPresets = Array.isArray(data.effectPresets) && data.effectPresets.length
      ? data.effectPresets
      : GLOBAL_EFFECT_PRESETS
    const textMotionPresets = Array.isArray(data.textMotionPresets) && data.textMotionPresets.length
      ? data.textMotionPresets
      : GLOBAL_TEXT_MOTION_PRESETS

    state.globalPresetCatalogRows = Array.isArray(data.rows) ? data.rows : []
    state.globalPresetCatalogLoaded = true
    state.globalPresetCatalogError = data.error || null
    state.globalEffectPresets = effectPresets
    state.globalTextMotionPresets = textMotionPresets
    state.globalNodeWorkflowPresets = Array.isArray(data.nodeWorkflowPresets)
      ? data.nodeWorkflowPresets
      : []
    state.globalPersonalityPresets = Array.isArray(data.personalityPresets)
      ? data.personalityPresets
      : []
    state.globalMoodPresets = Array.isArray(data.moodPresets)
      ? data.moodPresets
      : []
    state.effects = buildEffectCatalog(
      { ...VYBY_EFFECTS },
      effectPresets,
      fromStorage('vyby/effects', null)
    )
  },
  setGlobalPresetCatalogError: (state, error = null) => {
    state.globalPresetCatalogLoaded = true
    state.globalPresetCatalogError = error ? `${error}` : 'Failed to load Vyby presets'
    state.globalPresetCatalogRows = []
    state.globalEffectPresets = GLOBAL_EFFECT_PRESETS
    state.globalTextMotionPresets = GLOBAL_TEXT_MOTION_PRESETS
    state.globalNodeWorkflowPresets = []
    state.globalPersonalityPresets = []
    state.globalMoodPresets = []
    state.effects = buildEffectCatalog(
      { ...VYBY_EFFECTS },
      GLOBAL_EFFECT_PRESETS,
      fromStorage('vyby/effects', null)
    )
  },
  initialized: (state, isNew = true) => {
    state.isNew = isNew
    state.initialized = true
  },
  uuid: (state, next = null) => {
    state.uuid = next || Uuid()
  },
  saved: (state, next) => {
    state.saved = next
  },
  autosaveStatus: (state, next = {}) => {
    const status = next.status || 'idle'
    state.autosave = {
      ...(state.autosave || {}),
      status,
      error: next.error || null,
      lastSavedAt: typeof next.lastSavedAt !== 'undefined'
        ? next.lastSavedAt
        : state.autosave?.lastSavedAt || null,
    }
  },
  redirect: (state, next) => {
    state.redirect = next
  },
  reset: (state) => {
    // const t1 = Uuid()
    // const t2 = Uuid()
    // const t3 = Uuid()
    // const t4 = Uuid()
    // const t0 = Uuid()
    state.tracks = {
      [tracksIndex.globalRigTrackUuid]: defaultTrack(
        tracksIndex.globalRigTrackUuid,
        'global',
        99,
        'Rig',
        ['camera'],
        { collapsible: false, visible: false, hidden: true, locked: true }
      ),
      [tracksIndex.trackUuid1]: defaultTrack(
        tracksIndex.trackUuid1,
        'layer',
        1,
        'Front close',
        ['video', 'image', 'text', 'adjustment', 'model']
      ),
      [tracksIndex.trackUuid2]: defaultTrack(
        tracksIndex.trackUuid2,
        'layer',
        2,
        'Front behind',
        ['video', 'image', 'text', 'adjustment', 'model']
      ),
      [tracksIndex.trackUuid3]: defaultTrack(
        tracksIndex.trackUuid3,
        'layer',
        3,
        'Back close',
        ['video', 'image', 'text', 'adjustment', 'model']
      ),
      [tracksIndex.trackUuid4]: defaultTrack(
        tracksIndex.trackUuid4,
        'layer',
        4,
        'Back far',
        ['video', 'image', 'text', 'adjustment', 'model']
      ),
      [tracksIndex.audioTrackUuid]: defaultTrack(
        tracksIndex.audioTrackUuid,
        'audio',
        0,
        'Audio',
        ['audio']
      ),
    }
    // tracksIndex.trackUuid1 = t1
    // tracksIndex.trackUuid2 = t2
    // tracksIndex.trackUuid3 = t3
    // tracksIndex.trackUuid4 = t4
    // tracksIndex.audioTrackUuid = t0
    state.timeline = defaultTimeline()
    state.settings = defaultSettings()
    state.song = defaultSong()
    state.environment = defaultEnv()
    state.picture = defaultPicture()
    state.generated = {}
    state.library = {}
    state.effects = buildEffectCatalog(
      { ...VYBY_EFFECTS },
      state.globalEffectPresets || GLOBAL_EFFECT_PRESETS,
      fromStorage('vyby/effects', null)
    )
    state.bottomLaneMode = 'audio'
    state.audioLaneExpanded = false
    state.playing = false
    state.recording = false
    state.calculating = false
    state.lastPlaybackStartTime = 0
    state.clipRangeEdit = { active: false, stripId: null }
    state.textModal = { open: false, stripId: null, source: null }
    state.subclipModal = { open: false, stripId: null, source: null, payload: null }
    state.subclipModalResult = null
    state.autosave = { status: 'idle', error: null, lastSavedAt: null }
    state.assetHydration = {
      active: false,
      projectId: null,
      loaded: 0,
      total: 0,
      error: null,
    }
  },
  aiTask: (state, next) => {
    state.aiTask = next
  },
  aiTool: (state, next) => {
    state.aiTool = next
  },
  gridHelper: (state, next) => {
    state.gridHelper = next
  },
  projectTitle: (state, next) => {
    state.settings.title = next
  },
  updateSettings: (state, updates) => {
    if (updates && typeof updates === 'object') {
      Object.assign(state.settings, updates)
    }
  },
  setParamColor: (state, { code, color }) => {
    const key = canonicalParamColorCode(code)
    if (!key) return
    if (!state.settings.paramColors || typeof state.settings.paramColors !== 'object') {
      state.settings.paramColors = {}
    }
    state.settings.paramColors = {
      ...state.settings.paramColors,
      [key]: normalizeParamColor(color),
    }
    state.saved = false
  },
  flatView: (state, next) => {
    state.flatView = next
    state.effectsView = !state.flatView
    if (!state.effectsView) state.timeline.activeEffect = null
  },
  effectsView: (state, next) => {
    state.effectsView = next
    if (!next) state.timeline.activeEffect = null
    state.flatView = !state.effectsView
  },
  settingsView: (state, next) => {
    state.settingsView = next
  },
  bottomLaneMode: (state, next) => {
    state.bottomLaneMode = next === 'camera' ? 'camera' : 'audio'
  },
  audioLaneExpanded: (state, next) => {
    state.audioLaneExpanded = !!next
  },
  activeEffect: (state, next) => {
    state.timeline.activeEffect = next
  },
  upsertEffectPreset: (state, data) => {
    // console.log('UPSERT PRESET', data)
    // const uuid = data.uuid || Uuid()
    const { group, name, preset } = data
    if (!group || !name || !preset) return
    if (!state.effects[group]) {
      state.effects[group] = {
        name: `Custom <${group}>`,
        presets: {},
      }
    }
    state.effects[group].presets[data.code] = data
    toStorage('vyby/effects', serializeUserEffectCatalog(state.effects))
  },
  delEffectPreset: (state, { uuid, group }) => {
    if (!state.effects[group]) return
    if (!state.effects[group].presets[uuid]) return
    delete state.effects[group].presets[uuid]
    toStorage('vyby/effects', serializeUserEffectCatalog(state.effects))
  },
  activeAudio: (state, next) => {
    state.timeline.activeAudio = next
  },
  toggleSnap2Beat: (state, next) => {
    if (typeof next !== 'undefined') state.snap2beat = !!next
    else state.snap2beat = !state.snap2beat
  },
  snapUnit: (state, next) => {
    state.snapUnit = next
  },
  fullScreen: (state, next) => {
    const canvas3d = typeof document !== 'undefined'
      ? document.getElementById('three-canvas')
      : null
    state.fullScreenRect = canvas3d ? canvas3d.getBoundingClientRect() : null
    state.fullScreen = !!next
  },
  currentSelection: (state, next) => {
    state.currentSelection = next
  },
  clipRangeEdit: (state, next) => {
    const active = !!next?.active
    const stripId = active && next?.stripId ? next.stripId : null
    state.clipRangeEdit = { active, stripId }
  },
  textModal: (state, next) => {
    const open = !!next?.open
    const stripId = open && next?.stripId ? next.stripId : null
    const source = open && next?.source ? next.source : null
    state.textModal = { open, stripId, source }
  },
  subclipModal: (state, next) => {
    const open = !!next?.open
    const stripId = open && next?.stripId ? next.stripId : null
    const source = open && next?.source ? next.source : null
    const payload = open && next?.payload && typeof next.payload === 'object'
      ? { ...next.payload }
      : null
    state.subclipModal = { open, stripId, source, payload }
  },
  subclipModalResult: (state, next) => {
    state.subclipModalResult = next && typeof next === 'object'
      ? { ...next }
      : null
  },
  akfSource: (state, next) => {
    state.akfSource = next
  },
  akfValue: (state, { code, val }) => {
    state.akfValues[code] = val
  },
  resetAkf: (state) => {
    state.akfSource = null
    state.akfValues = {}
  },
  songUpdate: (state, upd) => {
    state.song = { ...state.song, ...upd }
    // state.settings.bpm = upd.bpm
    state.tuned = false
  },
  envUpdate: (state, upd) => {
    state.environment = { ...state.environment, ...(upd || null) }
  },
  pictureUpdate: (state, upd) => {
    state.picture = { ...state.picture, ...(upd || null) }
  },
  libraryMediaNew: (state, { uuid, type, meta = null }) => {
    state.library[uuid] = {
      uuid,
      type,
      meta: meta && typeof meta === 'object' ? { ...meta } : {},
      ready: false,
    }
  },
  libraryMediaData: (state, { uuid, data }) => {
    if (!state.library[uuid]) return
    state.library[uuid].meta = {
      ...(state.library[uuid].meta || {}),
      ...(data || {}),
    }
    state.library[uuid].ready = true
  },
  libraryMediaMeta: (state, { uuid, meta }) => {
    if (!uuid || !state.library[uuid]) return
    state.library[uuid].meta = {
      ...(state.library[uuid].meta || {}),
      ...(meta || {}),
    }
  },
  libraryMediaRemove: (state, uuid) => {
    if (!uuid || !state.library[uuid]) return
    delete state.library[uuid]
    state.saved = false
  },
  // Proxy video Phase 2: merge per-asset proxy detection/transcode state.
  videoProxyState: (state, { uuid, updates }) => {
    if (!uuid) return
    state.videoProxy[uuid] = {
      ...(state.videoProxy[uuid] || {}),
      ...(updates || {}),
    }
  },
  assetHydrationStart: (state, { projectId = null, total = 0 } = {}) => {
    state.assetHydration = {
      active: Number(total) > 0,
      projectId,
      loaded: 0,
      total: Math.max(0, Number(total) || 0),
      error: null,
    }
  },
  assetHydrationProgress: (state, { loaded = null, total = null } = {}) => {
    const current = state.assetHydration || {}
    const nextTotal = total == null
      ? Number(current.total) || 0
      : Math.max(0, Number(total) || 0)
    const nextLoaded = loaded == null
      ? Number(current.loaded) || 0
      : Math.max(0, Number(loaded) || 0)
    state.assetHydration = {
      ...current,
      active: nextLoaded < nextTotal,
      loaded: Math.min(nextLoaded, nextTotal),
      total: nextTotal,
    }
  },
  assetHydrationComplete: (state) => {
    const current = state.assetHydration || {}
    state.assetHydration = {
      ...current,
      active: false,
      loaded: Number(current.total) || Number(current.loaded) || 0,
      total: Number(current.total) || Number(current.loaded) || 0,
      error: null,
    }
  },
  assetHydrationError: (state, error = null) => {
    const current = state.assetHydration || {}
    state.assetHydration = {
      ...current,
      active: false,
      error: error ? `${error}` : 'Asset loading failed',
    }
  },
  gizmo: (state, next) => {
    state.gizmo = next
  },
  dnd: (state, next) => {
    state.dnd = next
  },
  zeroFrame3D: (state, next) => {
    state.timeline.zeroFrame3D = next
  },
  projectSetting: (state, { code, value }) => {
    if (state._pushUndo) state._pushUndo()
    state.settings[code] = value
    state.tuned = false
    state.saved = false
  },
  setNodeGraph: (state, next) => {
    // Graph edits can be frequent (dragging nodes). Avoid pushing undo snapshots for every move.
    state.settings.nodeGraph = next || null
    state.saved = false
  },
  applyTransformAgentSetKeyframes: (state, { payload } = {}) => {
    if (!isPlainObject(payload)) return
    const stripId =
      typeof payload.selectedStripId === 'string' ? payload.selectedStripId : ''
    if (!stripId) return

    const tuuid = Object.keys(state.tracks || {}).find(
      (id) => !!state.tracks?.[id]?.strips?.[stripId]
    )
    if (!tuuid) return
    const strip = state.tracks[tuuid].strips[stripId]
    if (!strip) return
    const scaleBaseline = readStripFitScaleBaseline(strip, {
      x: strip?.animation?.values?.['scale.x'],
      y: strip?.animation?.values?.['scale.y'],
      z: strip?.animation?.values?.['scale.z'],
    })

    if (state._pushUndo) state._pushUndo('transform-agent-set-keyframes')

    if (!strip.animation || typeof strip.animation !== 'object') {
      strip.animation = { uuid: Uuid(), keyframes: [], values: {} }
    }
    if (!Array.isArray(strip.animation.keyframes)) strip.animation.keyframes = []

    const fps = Math.max(1, Math.round(finiteNum(state.settings?.fps, 24)))
    const clipFrameCount = Math.max(
      1,
      Math.round(finiteNum(strip.length, 0) * fps)
    )
    const maxFrame = Math.max(0, clipFrameCount - 1)
    const toFrame = (frame) =>
      clampNum(Math.round(finiteNum(frame, 0)), 0, maxFrame)
    const toLocalTime = (frame) =>
      clampNum(
        toFrame(frame) / fps,
        0,
        Math.max(0, finiteNum(strip.length, 0))
      )
    const frameFromTime = (time) =>
      clampNum(Math.round(finiteNum(time, 0) * fps), 0, maxFrame)

    const writeMode = `${payload.writeMode || ''}`
    const isRangeReplace = writeMode === 'replace_transform_keys_in_range'
    const isReplaceAll = writeMode === 'replace_all_transform_keys'
    const shouldClear = isRangeReplace || isReplaceAll

    const rangeStart = isRangeReplace
      ? toFrame(payload?.range?.startFrame)
      : 0
    const rangeEndExclusive = isRangeReplace
      ? Math.max(rangeStart + 1, toFrame(payload?.range?.endFrameExclusive))
      : clipFrameCount

    const removeTransformPropsFromKeyframe = (kf) => {
      if (!isPlainObject(kf)) return null
      const next = jclone(kf)
      if (!isPlainObject(next.props)) next.props = {}
      if (!isPlainObject(next.delay)) next.delay = {}
      if (!isPlainObject(next.easing)) next.easing = {}
      if (!isPlainObject(next.interpOut)) next.interpOut = {}

      TRANSFORM_PROP_CODES.forEach((code) => {
        if (typeof next.props[code] !== 'undefined') delete next.props[code]
        if (typeof next.delay[code] !== 'undefined') delete next.delay[code]
        if (typeof next.easing[code] !== 'undefined') delete next.easing[code]
        if (typeof next.interpOut[code] !== 'undefined') delete next.interpOut[code]
      })

      const hasProps = Object.keys(next.props || {}).length > 0
      if (!hasProps && next.removable && !next.marker) return null
      return next
    }

    let nextKeyframes = jclone(strip.animation.keyframes)
    if (shouldClear) {
      nextKeyframes = nextKeyframes
        .map((kf) => {
          const frame = frameFromTime(kf?.time)
          const inRange =
            isReplaceAll ||
            (frame >= rangeStart && frame < rangeEndExclusive)
          if (!inRange) return kf
          return removeTransformPropsFromKeyframe(kf)
        })
        .filter(Boolean)
    }

    const ensureKeyframeAtFrame = (frame) => {
      const targetFrame = toFrame(frame)
      let idx = nextKeyframes.findIndex(
        (kf) => frameFromTime(kf?.time) === targetFrame
      )
      if (idx >= 0) return idx
      nextKeyframes.push({
        uuid: Uuid(),
        type: 'point',
        removable: true,
        time: toLocalTime(targetFrame),
        delay: {},
        props: {},
        easing: {},
      })
      idx = nextKeyframes.length - 1
      return idx
    }

    const mapEasing = (value) => EASING_TO_EDITOR[value] || 'Linear.None'
    const isHold = (value) => value === 'hold'
    const keyframes = Array.isArray(payload.keyframes) ? payload.keyframes : []
    keyframes.forEach((outputKf) => {
      if (!isPlainObject(outputKf)) return
      const idx = ensureKeyframeAtFrame(outputKf.frame)
      const kf = nextKeyframes[idx]
      if (!isPlainObject(kf.props)) kf.props = {}
      if (!isPlainObject(kf.delay)) kf.delay = {}
      if (!isPlainObject(kf.easing)) kf.easing = {}
      if (!isPlainObject(kf.interpOut)) kf.interpOut = {}

      const changedProps = []
      const transform = isPlainObject(outputKf.transform) ? outputKf.transform : {}

      if (isPlainObject(transform.position)) {
        ['x', 'y', 'z'].forEach((axis) => {
          if (typeof transform.position[axis] === 'undefined') return
          const code = `position.${axis}`
          kf.props[code] = finiteNum(transform.position[axis], 0)
          changedProps.push(code)
        })
      }

      if (isPlainObject(transform.scalePct)) {
        ['x', 'y', 'z'].forEach((axis) => {
          if (typeof transform.scalePct[axis] === 'undefined') return
          const code = `scale.${axis}`
          kf.props[code] = fitPercentToRawScale(
            finiteNum(transform.scalePct[axis], 100),
            scaleBaseline[axis]
          )
          changedProps.push(code)
        })
      }

      if (isPlainObject(transform.rotationDeg)) {
        ['x', 'y', 'z'].forEach((axis) => {
          if (typeof transform.rotationDeg[axis] === 'undefined') return
          const code = `rotation.${axis}`
          kf.props[code] = (finiteNum(transform.rotationDeg[axis], 0) * Math.PI) / 180
          changedProps.push(code)
        })
      }

      if (typeof outputKf.easingToNext !== 'undefined') {
        const easing = mapEasing(outputKf.easingToNext)
        const hold = isHold(outputKf.easingToNext)
        changedProps.forEach((code) => {
          if (typeof kf.delay[code] === 'undefined') kf.delay[code] = 0
          kf.easing[code] = easing
          kf.interpOut[code] = hold ? 'hold' : 'tween'
        })
      }
    })

    nextKeyframes = nextKeyframes
      .filter((kf) => {
        if (!isPlainObject(kf)) return false
        const hasProps = Object.keys(kf.props || {}).length > 0
        return hasProps || !kf.removable || !!kf.marker
      })
      .sort((a, b) => finiteNum(a.time, 0) - finiteNum(b.time, 0))

    strip.animation.keyframes = nextKeyframes
    // Keep runtime animation cache in sync with store writes from agent output.
    // Without this emit, preview/playback can continue using stale keyframe values
    // until a manual UI edit rebuilds the Animation instance.
    try {
      const asset = factory.asset(strip?.asset?.uuid)
      if (asset && typeof asset.emit === 'function') {
        asset.emit('updateAnimation', strip.animation)
      }
    } catch (e) {
      void e
    }
    state.timeline.activeTrack = tuuid
    state.timeline.activeStrip = stripId
    state.tuned = false
    state.saved = false
  },
  applyTransformKeyframePreset: (state, { stripId, preset, range = null } = {}) => {
    const normalized = normalizeTransformKeyframePreset(preset)
    if (!normalized || !stripId) return
    const tuuid = Object.keys(state.tracks || {}).find(
      (id) => !!state.tracks?.[id]?.strips?.[stripId]
    )
    if (!tuuid) return
    const strip = state.tracks[tuuid].strips[stripId]
    if (!strip) return
    if (!strip.animation || typeof strip.animation !== 'object') {
      strip.animation = { uuid: Uuid(), keyframes: [], values: {} }
    }
    if (!Array.isArray(strip.animation.keyframes)) strip.animation.keyframes = []

    const fps = Math.max(1, Math.round(finiteNum(state.settings?.fps, 24)))
    const clipFrameCount = Math.max(1, Math.round(finiteNum(strip.length, 0) * fps))
    const propsToReplace = new Set(normalized.supportedProps || [])
    const startFrame = range
      ? clampNum(Math.round(finiteNum(range.startFrame, 0)), 0, clipFrameCount - 1)
      : 0
    const endFrameExclusive = range
      ? Math.max(
          startFrame + 1,
          clampNum(Math.round(finiteNum(range.endFrameExclusive, clipFrameCount)), 1, clipFrameCount)
        )
      : clipFrameCount
    const frameFromTime = (time) =>
      clampNum(Math.round(finiteNum(time, 0) * fps), 0, clipFrameCount - 1)
    const toLocalTime = (frame) =>
      clampNum(
        clampNum(Math.round(finiteNum(frame, 0)), 0, clipFrameCount - 1) / fps,
        0,
        Math.max(0, finiteNum(strip.length, 0))
      )
    const applyFrames = buildTransformKeyframePresetApplyFrames({
      preset: normalized,
      strip,
      fps,
      range: { startFrame, endFrameExclusive },
    })
    if (!applyFrames.length) return

    if (state._pushUndo) state._pushUndo('transform-keyframe-preset')

    let nextKeyframes = jclone(strip.animation.keyframes)
      .map((kf) => {
        const frame = frameFromTime(kf?.time)
        if (frame < startFrame || frame >= endFrameExclusive) return kf
        const next = jclone(kf)
        if (!isPlainObject(next.props)) next.props = {}
        if (!isPlainObject(next.delay)) next.delay = {}
        if (!isPlainObject(next.easing)) next.easing = {}
        if (!isPlainObject(next.interpOut)) next.interpOut = {}
        propsToReplace.forEach((prop) => {
          delete next.props[prop]
          delete next.delay[prop]
          delete next.easing[prop]
          delete next.interpOut[prop]
        })
        const hasProps = Object.keys(next.props || {}).length > 0
        if (!hasProps && next.removable && !next.marker) return null
        return next
      })
      .filter(Boolean)

    const ensureKeyframeAtFrame = (frame) => {
      const targetFrame = clampNum(Math.round(finiteNum(frame, 0)), 0, clipFrameCount - 1)
      let idx = nextKeyframes.findIndex((kf) => frameFromTime(kf?.time) === targetFrame)
      if (idx >= 0) return idx
      nextKeyframes.push({
        uuid: Uuid(),
        type: 'point',
        removable: true,
        time: toLocalTime(targetFrame),
        delay: {},
        props: {},
        easing: {},
        interpOut: {},
      })
      return nextKeyframes.length - 1
    }

    applyFrames.forEach((item) => {
      const idx = ensureKeyframeAtFrame(item.frame)
      const kf = nextKeyframes[idx]
      if (!isPlainObject(kf.props)) kf.props = {}
      if (!isPlainObject(kf.delay)) kf.delay = {}
      if (!isPlainObject(kf.easing)) kf.easing = {}
      if (!isPlainObject(kf.interpOut)) kf.interpOut = {}
      Object.entries(item.props || {}).forEach(([prop, value]) => {
        kf.props[prop] = value
        if (typeof kf.delay[prop] === 'undefined') kf.delay[prop] = 0
        kf.easing[prop] = item.easing?.[prop] || kf.easing[prop] || 'Linear.None'
        if (item.interpOut?.[prop]) kf.interpOut[prop] = item.interpOut[prop]
      })
    })

    strip.animation.keyframes = nextKeyframes
      .filter((kf) => Object.keys(kf.props || {}).length || !kf.removable || !!kf.marker)
      .sort((a, b) => finiteNum(a.time, 0) - finiteNum(b.time, 0))
    try {
      const asset = factory.asset(strip?.asset?.uuid)
      if (asset && typeof asset.emit === 'function') {
        asset.emit('updateAnimation', strip.animation)
      }
    } catch (e) {
      void e
    }
    state.timeline.activeTrack = tuuid
    state.timeline.activeStrip = stripId
    state.tuned = false
    state.saved = false
  },
  moveKeyframe: (state, { tuuid, suuid, kfuuid, toTime }) => {
    if (state._pushUndo) state._pushUndo()
    if (!state.tracks[tuuid]) return
    if (!state.tracks[tuuid].strips[suuid]) return
    const strip = state.tracks[tuuid].strips[suuid]
    const v2 = !!state.settings.legacyKeyframesV2
    for (
      let i = 0;
      i < state.tracks[tuuid].strips[suuid].animation.keyframes.length;
      i++
    ) {
      if (
        state.tracks[tuuid].strips[suuid].animation.keyframes[i].uuid ===
        kfuuid
      ) {
        const kf = state.tracks[tuuid].strips[suuid].animation.keyframes[i]
        if (!v2) {
          Object.keys(kf.props).forEach((prop) => {
            const diff = toTime - kf.time
            if (kf.delay[prop] > diff)
              state.tracks[tuuid].strips[suuid].animation.keyframes[i].delay[
                prop
              ] -= diff
          })
        }
        /*
        const diff = toTime - kf.time
        const endTime = kfn ? kfn.time : strip.length
        Object.keys(kf.props).forEach(prop => {
          if (kf.delay[prop] + toTime <= endTime) return
          const ndelay = kf.delay[prop] - diff
          state.tracks[tuuid].strips[suuid].animation.keyframes[i].delay[prop] = ndelay
        })
        */
        state.tracks[tuuid].strips[suuid].animation.keyframes[i].time = toTime
      }
    }
    state.timeline.activeKeyframe = kfuuid
    state.timeline.currentTime = strip.start + toTime
    state.tuned = false
  },
  addEmptyKeyframe: (state, payload) => {
    if (state._pushUndo) state._pushUndo()
    if (!state.timeline.activeTrack || !state.timeline.activeStrip) return
    const strip =
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ]
    if (!strip) return
    const time = typeof payload === 'number' ? payload : payload?.time
    const marker = typeof payload === 'number' ? true : payload?.marker !== false
    if (!Number.isFinite(time)) return
    const t = time - strip.start
    const fps = Number(state.settings?.fps) || 30
    const eps = 1 / fps / 2
    if (strip.animation.keyframes.some((kf) => Math.abs(kf.time - t) < eps))
      return
    const next = jclone(strip.animation.keyframes)
    const uuid = Uuid()
    next.push({
      uuid: uuid,
      type: 'point',
      removable: true,
      time: t,
      marker: !!marker,
      delay: {},
      props: {},
      easing: {},
    })
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes = next.sort((a, b) => a.time - b.time)
    state.timeline.activeKeyframe = uuid
  },
  removeSelectedKeyframe: (state) => {
    if (state._pushUndo) state._pushUndo()
    if (
      !state.timeline.activeTrack ||
      !state.timeline.activeStrip ||
      !state.timeline.activeKeyframe
    )
      return
    const track = state.tracks[state.timeline.activeTrack]
    if (!track) return
    const strip =
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ]
    if (
      !strip ||
      !strip.animation.keyframes.some(
        (kf) => kf.uuid === state.timeline.activeKeyframe && kf.removable
      )
    )
      return
    const next = jclone(strip.animation.keyframes).filter(
      (kf) => kf.uuid !== state.timeline.activeKeyframe
    )
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes = next.sort((a, b) => a.time - b.time)
    state.tuned = false
  },
  // Delete keyframes like NLEs: delete only the selected lane props (or parent-mapped props).
  // If no lane selection exists, fall back to deleting the whole keyframe (existing behavior).
  removeSelectedKeyframeSelection: (state) => {
    if (state._pushUndo) state._pushUndo()
    if (
      !state.timeline.activeTrack ||
      !state.timeline.activeStrip ||
      !state.timeline.activeKeyframe
    )
      return
    const track = state.tracks[state.timeline.activeTrack]
    if (!track) return
    const strip = track.strips[state.timeline.activeStrip]
    if (!strip) return

    const sel = state.timeline.activeKeyframeSelection
    const hasSelProps =
      sel &&
      sel.uuid === state.timeline.activeKeyframe &&
      Array.isArray(sel.props) &&
      sel.props.length

    const kfi = strip.animation.keyframes.findIndex(
      (kf) => kf.uuid === state.timeline.activeKeyframe
    )
    if (kfi < 0) return
    const kf = strip.animation.keyframes[kfi]

    // Only operate when playhead is exactly on the selected keyframe time
    // (prevents accidental deletion while scrubbing).
    const absTime = strip.start + kf.time
    if (state.timeline.currentTime !== absTime) return

    if (hasSelProps) {
      const hasAny = (code) =>
        (kf.props && typeof kf.props[code] !== 'undefined') ||
        (kf.delay && typeof kf.delay[code] !== 'undefined') ||
        (kf.easing && typeof kf.easing[code] !== 'undefined') ||
        (kf.interpOut && typeof kf.interpOut[code] !== 'undefined')

      const propsSet = new Set(sel.props || [])

      // Scale parent delete should include Z if it exists at this keyframe
      if (sel.lane === 'scale' && hasAny('scale.z')) propsSet.add('scale.z')

      const props = Array.from(propsSet)
      props.forEach((code) => {
        if (kf.props && typeof kf.props[code] !== 'undefined') delete kf.props[code]
        if (kf.delay && typeof kf.delay[code] !== 'undefined') delete kf.delay[code]
        if (kf.easing && typeof kf.easing[code] !== 'undefined') delete kf.easing[code]
        if (kf.interpOut && typeof kf.interpOut[code] !== 'undefined')
          delete kf.interpOut[code]
      })

      // If this keyframe no longer affects any props, remove it (only if removable).
      const remaining = Object.keys(kf.props || {}).length
      if (remaining === 0 && kf.removable && !kf.marker) {
        const next = jclone(strip.animation.keyframes).filter(
          (kk) => kk.uuid !== state.timeline.activeKeyframe
        )
        state.tracks[state.timeline.activeTrack].strips[
          state.timeline.activeStrip
        ].animation.keyframes = next.sort((a, b) => a.time - b.time)
        state.timeline.activeKeyframe = null
      }

      state.timeline.activeKeyframeSelection = null
      state.tuned = false
      return
    }

    // No selection: delete entire keyframe (existing behavior)
    if (
      !strip.animation.keyframes.some(
        (kk) => kk.uuid === state.timeline.activeKeyframe && kk.removable
      )
    )
      return
    const next = jclone(strip.animation.keyframes).filter(
      (kk) => kk.uuid !== state.timeline.activeKeyframe
    )
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes = next.sort((a, b) => a.time - b.time)
    state.timeline.activeKeyframe = null
    state.timeline.activeKeyframeSelection = null
    state.tuned = false
  },
  tool: (state, next) => {
    // looking if current time points to keyframe
    /*
    const ntool = state.tool === next ? null : next
    if (ntool && state.timeline.activeTrack && state.timeline.activeStrip) {
      const strip =
        state.tracks[state.timeline.activeTrack].strips[
          state.timeline.activeStrip
        ]
      const lkf = strip.animation.keyframes.reduce((acc, kf) => {
        if (kf.time + strip.start <= state.timeline.currentTime) return kf
        return acc
      }, strip.animation.keyframes[0])
      // console.log('LKF', lkf);
      state.timeline.activeKeyframe = lkf.uuid
      // move current time to most close keyframe from bottom
      if (state.timeline.currentTime !== lkf.time + strip.start) {
        state.timeline.currentTime = strip.start + lkf.time
        // console.log('::tool::', lkf, state.timeline.currentTime);
      }
    }
    // if (!ntool) {
      // state.timeline.activeKeyframe = null
    // }
    */
    log.info('store!tool!', next)
    state.tool = next
  },
  toggleTween: (state, { prop, val }) => {
    if (
      !state.timeline.activeTrack ||
      !state.timeline.activeStrip ||
      !state.timeline.activeKeyframe
    )
      return
    if (
      !state.tracks[state.timeline.activeTrack] ||
      !state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ]
    )
      return
    const kfi = state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes.findIndex(
      (el) => el.uuid === state.timeline.activeKeyframe
    )
    if (kfi < 0) return
    const kf =
      state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes[kfi]
    const v2 = !!state.settings.legacyKeyframesV2
    if (v2) {
      // Phase 2: V2 toggles interpOut mode for the outgoing segment at this key
      if (!kf.interpOut) kf.interpOut = {}
      const mode = kf.interpOut[prop] === 'hold' ? 'tween' : 'hold'
      kf.interpOut[prop] = mode
      // Ensure prop exists when enabling tween so the evaluator knows the value
      if (mode === 'tween' && typeof kf.props[prop] === 'undefined') {
        kf.props[prop] = val
        if (typeof kf.delay[prop] === 'undefined') kf.delay[prop] = 0
        if (!kf.easing) kf.easing = {}
        if (!kf.easing[prop]) kf.easing[prop] = 'Linear.None'
      }
      state.tuned = false
      return
    }
    // remove tween
    if (typeof kf.props[prop] !== 'undefined') {
      delete state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes[kfi].props[prop]
      delete state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes[kfi].delay[prop]
      delete state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes[kfi].easing[prop]
      return
    }
    // add tween
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes[kfi].easing[prop] = 'Linear.None'
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes[kfi].delay[prop] = 0
    state.tracks[state.timeline.activeTrack].strips[
      state.timeline.activeStrip
    ].animation.keyframes[kfi].props[prop] = val
    // check if there was tween from previous keyframe and modify it
    if (kfi > 0) {
      const kfpi = state.tracks[state.timeline.activeTrack].strips[
        state.timeline.activeStrip
      ].animation.keyframes.findLastIndex((el) => {
        return typeof el.props[prop] !== 'undefined' && el.time < kf.time
      })
      // console.log('toggleTween', kfpi)
      // no tweens for prop before
      if (kfpi < 0) return
      const kfp =
        state.tracks[state.timeline.activeTrack].strips[
          state.timeline.activeStrip
        ].animation.keyframes[kfpi]
      const dt = kf.time - kfp.time
      if (kfp.delay[prop] > dt) {
        state.tracks[state.timeline.activeTrack].strips[
          state.timeline.activeStrip
        ].animation.keyframes[kfpi].delay[prop] = Math.floor(dt / 1000)
      }
    }
    state.tuned = false
  },
  // Add/remove a key on a specific prop at the active keyframe time.
  // This is "key existence" toggle (Premiere/Resolve style), not segment hold/tween toggle.
  toggleKeyProp: (state, { prop, val }) => {
    if (
      !state.timeline.activeTrack ||
      !state.timeline.activeStrip ||
      !state.timeline.activeKeyframe
    )
      return
    const track = state.tracks[state.timeline.activeTrack]
    if (!track) return
    const strip = track.strips[state.timeline.activeStrip]
    if (!strip) return
    // Text strips use SmartKeys for numeric envelope props regardless of global toggle.
    // Block legacy key toggles for those props to avoid creating unusable keyframes.
    try {
      const isText = !!(strip.asset && strip.asset.type === 'text')
      if (isText) {
        const transformAxes = [
          'position.x','position.y','position.z',
          'rotation.x','rotation.y','rotation.z',
          'scale.x','scale.y','scale.z'
        ]
        const isSmartManaged = transformAxes.includes(prop) || prop === 'opacity'
        if (isSmartManaged) return
      }
    } catch (e) { /* ignore gating errors */ }
    const kfi = strip.animation.keyframes.findIndex(
      (kf) => kf.uuid === state.timeline.activeKeyframe
    )
    if (kfi < 0) return
    const kf = strip.animation.keyframes[kfi]
    const v2 = !!state.settings.legacyKeyframesV2

    if (!kf.props) kf.props = {}
    if (!kf.delay) kf.delay = {}
    if (!kf.easing) kf.easing = {}
    if (v2 && !kf.interpOut) kf.interpOut = {}

    const has = typeof kf.props[prop] !== 'undefined'

    if (has) {
      delete kf.props[prop]
      delete kf.delay[prop]
      delete kf.easing[prop]
      if (v2 && kf.interpOut) delete kf.interpOut[prop]
      // If this keyframe is not an intentional marker and now has no keys, remove it.
      if (!kf.marker && kf.removable && Object.keys(kf.props || {}).length === 0) {
        const next = jclone(strip.animation.keyframes).filter((kk) => kk.uuid !== kf.uuid)
        track.strips[state.timeline.activeStrip].animation.keyframes = next.sort((a, b) => a.time - b.time)
        state.timeline.activeKeyframe = null
        state.timeline.activeKeyframeSelection = null
      }
      state.tuned = false
      return
    }

    kf.props[prop] = val
    if (typeof kf.delay[prop] === 'undefined') kf.delay[prop] = 0
    if (typeof kf.easing[prop] === 'undefined') kf.easing[prop] = 'Linear.None'
    if (v2 && kf.interpOut) kf.interpOut[prop] = 'tween'
    state.tuned = false
  },
  addEffect: (state, { effect, suuid, tuuid = null }) => {
    let strip = suuid || null
    let track = tuuid || null
    if (!track && state.timeline.activeStrip === suuid) {
      strip = suuid
      track = state.timeline.activeTrack
    }
    if (!track || !strip) return
    state.tracks[track].strips[strip].effects[effect.uuid] = effect
    state.timeline.activeEffect = effect.uuid
    /*
    if (tuuid && state.tracks[tuuid] && state.tracks[tuuid].strips[suuid]) {
      state.tracks[tuuid].strips[suuid].effects[euuid] = true
      state.timeline.activeEffect = euuid
      return
    }
    if (state.timeline.activeStrip === suuid) {
      state.tracks[state.timeline.activeTrack].strips[suuid].effects[euuid] = true
      state.timeline.activeEffect = euuid
      return
    }
    */
    state.tuned = false
  },
  updateEffect: (state, { tuuid, suuid, euuid, data }) => {
    if (tuuid && state.tracks[tuuid] && state.tracks[tuuid].strips[suuid]) {
      state.tracks[tuuid].strips[suuid].effects[euuid] = {
        ...state.tracks[tuuid].strips[suuid].effects[euuid],
        ...data,
      }
      const e = factory.effect(euuid)
      if (e) e.configure(data)
    }
    state.tuned = false
  },
  removeEffect: (state, { euuid, suuid, tuuid = null }) => {
    factory.removeEffect(euuid)
    if (tuuid && state.tracks[tuuid] && state.tracks[tuuid].strips[suuid]) {
      state.tracks[tuuid].strips[suuid].effects[euuid] = null
      state.timeline.activeEffect = null
      return
    }
    if (state.timeline.activeStrip === suuid) {
      state.tracks[state.timeline.activeTrack].strips[suuid].effects[euuid] =
        null
      state.timeline.activeEffect = null
      return
    }
    state.tuned = false
  },
}

